/**
 * Replaces the legacy jQuery accordion in component-accordion.js.
 */

/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Self-contained trigger + content disclosure.
 *
 * Expects:
 *   - Trigger:     [data-ref="trigger"] with aria-controls pointing to a panel ID
 *   - Panel:       [data-ref="panel"]
 *
 * Attributes:
 *   - dismissible - Escape closes and returns focus to the trigger. Clicking outside the item closes it.
 */
class AccordionItem extends HTMLElement {
  #trigger = null;
  #panel = null;

  connectedCallback() {
    this.#trigger = this.querySelector('[data-ref="trigger"]');
    this.#panel = this.querySelector('[data-ref="panel"]');

    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#panel?.setAttribute('inert', '');

    this.#trigger?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);

    this.#trigger = null;
    this.#panel = null;
  }

  get #isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  get #isDismissible() {
    return this.hasAttribute('dismissible');
  }

  open() {
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.toggleAttribute('open', true);
    this.#panel.removeAttribute('inert');

    this.closest('accordion-group')?.closeOthers(this);

    if (this.#isDismissible) {
      document.addEventListener('keydown', this.#onKeyDown);
      document.addEventListener('click', this.#onDocumentClick);
    }
  }

  close() {
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.toggleAttribute('open', false);
    this.#panel.setAttribute('inert', '');

    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  toggle() {
    this.#isOpen ? this.close() : this.open();
  }

  #onClick = () => this.toggle();

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    this.#trigger?.focus();
  };

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.close();
  };
}

if (!customElements.get('accordion-item')) {
  customElements.define('accordion-item', AccordionItem);
}

/**
 * @class AccordionGroup
 * @extends HTMLElement
 *
 * Optional wrapper that coordinates disclosure-item behavior into an
 * accordion (see the terminology note at the top of this file).
 *
 * Attributes:
 *   - data-mode="multiple" - Multiple items can be open simultaneously (default)
 *   - data-mode="single"   - Only one item open at a time
 */
class AccordionGroup extends HTMLElement {
  get #mode() {
    return this.dataset.mode === 'single' ? 'single' : 'multiple';
  }

  closeOthers(caller) {
    if (this.#mode === 'multiple') return;

    this.querySelectorAll('disclosure-item').forEach((item) => {
      if (item !== caller) item.close();
    });
  }
}

if (!customElements.get('accordion-group')) {
  customElements.define('accordion-group', AccordionGroup);
}
